// src/components/manager/shared/AssetFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, Edit, Trash2, Search, Filter, MapPin, X,
  Calendar, DollarSign, Wrench, AlertTriangle, CheckCircle,
  Truck, Monitor, Car, Hammer, Zap, Settings
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';
import { organisationService } from '../../../services/organisationService';

const ASSET_CATEGORIES = {
  facility: { label: 'Facilities', icon: Building2, color: 'blue' },
  vehicle: { label: 'Vehicles', icon: Car, color: 'green' },
  equipment: { label: 'Equipment', icon: Hammer, color: 'purple' },
  technology: { label: 'Technology', icon: Monitor, color: 'indigo' },
  utility: { label: 'Utilities', icon: Zap, color: 'yellow' },
  other: { label: 'Other', icon: Settings, color: 'gray' }
};

const ASSET_STATUS = {
  active: { label: 'Active', color: 'green', icon: CheckCircle },
  maintenance: { label: 'Maintenance', color: 'yellow', icon: Wrench },
  retired: { label: 'Retired', color: 'red', icon: AlertTriangle },
  reserved: { label: 'Reserved', color: 'blue', icon: Calendar }
};

export const AssetFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    category: 'equipment',
    status: 'active',
    description: '',
    location: '',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: '',
    currentValue: '',
    warranty: '',
    maintenanceSchedule: 'monthly',
    lastMaintenance: '',
    nextMaintenance: '',
    assignedTo: '',
    notes: '',
    images: [],
    documents: []
  });

  useEffect(() => {
    loadAssetData();
  }, [bookId]);

  const loadAssetData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'assets');
      setAssets(data?.assets || []);
    } catch (error) {
      console.error('Error loading asset data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAssetData = async (updatedAssets) => {
    try {
      await organisationService.updateManagementFeatureData(bookId, 'assets', {
        assets: updatedAssets,
        lastUpdated: new Date(),
        totalAssets: updatedAssets.length,
        activeAssets: updatedAssets.filter(a => a.status === 'active').length,
        maintenanceRequired: updatedAssets.filter(a => 
          new Date(a.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        ).length
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error saving asset data:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const assetData = {
        ...formData,
        id: editingAsset?.id || Date.now().toString(),
        createdAt: editingAsset?.createdAt || new Date(),
        updatedAt: new Date(),
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : 0,
        currentValue: formData.currentValue ? parseFloat(formData.currentValue) : 0
      };

      let updatedAssets;
      if (editingAsset) {
        updatedAssets = assets.map(asset => 
          asset.id === editingAsset.id ? assetData : asset
        );
      } else {
        updatedAssets = [...assets, assetData];
      }

      await saveAssetData(updatedAssets);
      setAssets(updatedAssets);
      handleCloseForm();
    } catch (error) {
      alert('Error saving asset: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const updatedAssets = assets.filter(asset => asset.id !== assetId);
      await saveAssetData(updatedAssets);
      setAssets(updatedAssets);
    } catch (error) {
      alert('Error deleting asset: ' + error.message);
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData(asset);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAsset(null);
    setFormData({
      name: '', category: 'equipment', status: 'active', description: '', location: '',
      serialNumber: '', purchaseDate: '', purchasePrice: '', currentValue: '',
      warranty: '', maintenanceSchedule: 'monthly', lastMaintenance: '', nextMaintenance: '',
      assignedTo: '', notes: '', images: [], documents: []
    });
  };

  const handleImageUpload = async (files, error) => {
    if (error) {
      alert('Error uploading images: ' + error);
      return;
    }

    try {
      const results = await firestoreService.uploadMultipleFiles(
        Array.isArray(files) ? files : [files],
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/assets/images`),
        (current, total, result, error) => {
          if (error) console.error('Upload error:', error);
        }
      );

      const successfulUploads = results.filter(r => r.success !== false);
      const newImages = successfulUploads.map(result => ({
        url: result.url,
        fileName: result.fileName,
        uploadedAt: new Date()
      }));

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
    } catch (error) {
      alert('Error uploading images: ' + error.message);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || asset.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="h-7 w-7 text-blue-600" />
            Asset Management
          </h3>
          <p className="text-gray-600 mt-1">Track facilities, equipment, vehicles, and resources</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Asset
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Assets</p>
              <p className="text-2xl font-bold text-gray-900">{assets.length}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Assets</p>
              <p className="text-2xl font-bold text-green-600">
                {assets.filter(a => a.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Maintenance Due</p>
              <p className="text-2xl font-bold text-yellow-600">
                {assets.filter(a => 
                  a.nextMaintenance && new Date(a.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                ).length}
              </p>
            </div>
            <Wrench className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-purple-600">
                R{assets.reduce((sum, asset) => sum + (asset.currentValue || 0), 0).toLocaleString()}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {Object.entries(ASSET_CATEGORIES).map(([key, cat]) => (
            <option key={key} value={key}>{cat.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {Object.entries(ASSET_STATUS).map(([key, status]) => (
            <option key={key} value={key}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAssets.map(asset => {
          const categoryConfig = ASSET_CATEGORIES[asset.category] || ASSET_CATEGORIES.other;
          const statusConfig = ASSET_STATUS[asset.status] || ASSET_STATUS.active;
          const CategoryIcon = categoryConfig.icon;
          const StatusIcon = statusConfig.icon;
          
          // Check if maintenance is due soon
          const maintenanceDue = asset.nextMaintenance && 
            new Date(asset.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          
          return (
            <div key={asset.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Asset Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${categoryConfig.color}-100`}>
                    <CategoryIcon className={`h-5 w-5 text-${categoryConfig.color}-600`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{asset.name}</h4>
                    <p className="text-sm text-gray-600">{categoryConfig.label}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.label}
                  </div>
                  {maintenanceDue && (
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Maintenance Due" />
                  )}
                </div>
              </div>

              {/* Asset Image */}
              {asset.images && asset.images.length > 0 && (
                <div className="mb-4">
                  <img 
                    src={asset.images[0].url} 
                    alt={asset.name}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Asset Details */}
              <div className="space-y-2 mb-4">
                {asset.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{asset.description}</p>
                )}
                {asset.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {asset.location}
                  </div>
                )}
                {asset.serialNumber && (
                  <p className="text-sm text-gray-600">S/N: {asset.serialNumber}</p>
                )}
                {asset.currentValue && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <DollarSign className="h-4 w-4" />
                    R{asset.currentValue.toLocaleString()}
                  </div>
                )}
              </div>

              {/* Maintenance Info */}
              {asset.nextMaintenance && (
                <div className={`p-3 rounded-lg mb-4 ${
                  maintenanceDue 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className={`h-4 w-4 ${maintenanceDue ? 'text-red-600' : 'text-gray-600'}`} />
                    <span className={maintenanceDue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                      Next maintenance: {new Date(asset.nextMaintenance).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {asset.assignedTo && (
                    <span>Assigned to: {asset.assignedTo}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(asset)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(asset.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAssets.length === 0 && (
        <div className="text-center py-8">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No assets found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first asset.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Asset
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  {editingAsset ? 'Edit Asset' : 'Add Asset'}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Asset Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(ASSET_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(ASSET_STATUS).map(([key, status]) => (
                        <option key={key} value={key}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Building, room, or area"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Detailed description of the asset..."
                  />
                </div>

                {/* Financial Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                    <input
                      type="date"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price (R)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.purchasePrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchasePrice: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Value (R)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.currentValue}
                      onChange={(e) => setFormData(prev => ({ ...prev, currentValue: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Technical Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                    <input
                      type="text"
                      value={formData.serialNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                    <input
                      type="text"
                      value={formData.assignedTo}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Person or department"
                    />
                  </div>
                </div>

                {/* Maintenance Information */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Maintenance Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Schedule</label>
                      <select
                        value={formData.maintenanceSchedule}
                        onChange={(e) => setFormData(prev => ({ ...prev, maintenanceSchedule: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="biannually">Bi-annually</option>
                        <option value="annually">Annually</option>
                        <option value="as_needed">As Needed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Maintenance</label>
                      <input
                        type="date"
                        value={formData.lastMaintenance}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastMaintenance: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Next Maintenance</label>
                      <input
                        type="date"
                        value={formData.nextMaintenance}
                        onChange={(e) => setFormData(prev => ({ ...prev, nextMaintenance: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Asset Images</h4>
                  
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    accept="image/*"
                    multiple
                    maxSize={5}
                    className="mb-4"
                  >
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Upload Asset Images</p>
                      <p className="text-sm text-gray-500">Drag & drop or click to select multiple images</p>
                    </div>
                  </FileUpload>

                  {formData.images && formData.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={image.url} 
                            alt={`Asset ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Additional notes, maintenance history, or special instructions..."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {editingAsset ? 'Update Asset' : 'Add Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};